/**
 * Utility for mapping API errors to user-friendly messages.
 */

export interface ApiError {
  status?: number;
  statusText?: string;
  data?: {
    detail?: string | any;
    message?: string;
  };
  message?: string;
}

/**
 * Map an API error to a user-friendly message.
 *
 * @param error - The error object from the API
 * @param defaultMessage - Default message if no specific mapping is found
 * @returns User-friendly error message
 */
export function mapErrorToMessage(
  error: any,
  defaultMessage: string = 'An unexpected error occurred. Please try again.'
): string {
  // Handle axios error structure
  if (error.response) {
    const status = error.response.status;
    const detail = error.response.data?.detail;

    // Check for specific error detail message
    if (typeof detail === 'string') {
      return detail;
    }

    // Map by status code
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';

      case 401:
        return 'You are not authorized to perform this action. Please log in again.';

      case 403:
        return 'You do not have permission to perform this action.';

      case 404:
        return 'The requested resource was not found.';

      case 409:
        return 'This entity already exists. Please use a different name.';

      case 422:
        // Validation errors
        if (Array.isArray(detail)) {
          const validationMessages = detail.map((err: any) => {
            const field = err.loc?.join('.') || 'field';
            const msg = err.msg || 'is invalid';
            return `${field}: ${msg}`;
          });
          return `Validation error: ${validationMessages.join(', ')}`;
        }
        return 'Validation error. Please check your input.';

      case 429:
        return 'Too many requests. Please wait a moment and try again.';

      case 500:
        return 'Internal server error. Please contact support if this persists.';

      case 502:
        return 'Server is temporarily unavailable. Please try again later.';

      case 503:
        return 'Service is currently unavailable. Please try again later.';

      case 504:
        return 'Request timed out. Please try again.';

      default:
        return error.response.data?.message || defaultMessage;
    }
  }

  // Handle network errors
  if (error.request && !error.response) {
    return 'Network error. Please check your internet connection and try again.';
  }

  // Handle error message directly
  if (error.message) {
    // Check for common error patterns
    if (error.message.toLowerCase().includes('network')) {
      return 'Network error. Please check your internet connection.';
    }
    if (error.message.toLowerCase().includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    return error.message;
  }

  // Fallback to default message
  return defaultMessage;
}

/**
 * Get a user-friendly title for an error based on its type.
 *
 * @param error - The error object from the API
 * @returns Error title
 */
export function getErrorTitle(error: any): string {
  if (error.response) {
    const status = error.response.status;

    switch (status) {
      case 400:
        return 'Invalid Request';
      case 401:
        return 'Authentication Required';
      case 403:
        return 'Permission Denied';
      case 404:
        return 'Not Found';
      case 409:
        return 'Conflict';
      case 422:
        return 'Validation Error';
      case 429:
        return 'Rate Limit Exceeded';
      case 500:
        return 'Server Error';
      case 502:
      case 503:
        return 'Service Unavailable';
      case 504:
        return 'Timeout';
      default:
        return 'Error';
    }
  }

  if (error.request && !error.response) {
    return 'Network Error';
  }

  return 'Error';
}

/**
 * Check if an error is retryable (transient network error).
 *
 * @param error - The error object from the API
 * @returns True if the error is retryable
 */
export function isRetryableError(error: any): boolean {
  // Network errors are retryable
  if (error.request && !error.response) {
    return true;
  }

  // Certain status codes are retryable
  if (error.response) {
    const status = error.response.status;
    return status === 408 || status === 429 || status === 500 || status === 502 || status === 503 || status === 504;
  }

  // Timeout errors are retryable
  if (error.message && error.message.toLowerCase().includes('timeout')) {
    return true;
  }

  return false;
}

/**
 * Extract validation errors from a 422 response.
 *
 * @param error - The error object from the API
 * @returns Map of field names to error messages
 */
export function extractValidationErrors(error: any): Record<string, string> {
  const errors: Record<string, string> = {};

  if (error.response?.status === 422 && error.response?.data?.detail) {
    const detail = error.response.data.detail;

    if (Array.isArray(detail)) {
      detail.forEach((err: any) => {
        const field = err.loc?.[err.loc.length - 1] || 'unknown';
        const message = err.msg || 'Invalid value';
        errors[field] = message;
      });
    }
  }

  return errors;
}

/**
 * Retry a function with exponential backoff.
 *
 * @param fn - Function to retry
 * @param maxRetries - Maximum number of retries
 * @param baseDelay - Base delay in milliseconds
 * @returns Result of the function
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: any;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Only retry if it's a retryable error
      if (!isRetryableError(error)) {
        throw error;
      }

      // Don't wait after the last retry
      if (i < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, i);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}
